<template>
  <section v-if="showSkillDive && !isFetching" class="labs-experience">
    <LabsHighlightedCarrousel class="labs-experience__highlighted" />
    <FeaturedCollectionsSection is-homepage />
    <div class="labs-experience__search-section">
      <div class="labs-experience__refinement">
        <SearchInput
          sortingDropdown
          :sortingOptions="sortingOptions"
          :sortingSelected="sortingSelected"
          class="labs-experience__search-section-input"
          @handle-sorting="updateSort"
        />
        <div class="labs-experience__search-section-results-wrapper">
          {{ searchLabel }}
        </div>
        <FilterTags
          :currentRefinements="currentRefinements"
          :filterMap="filterTagsMap"
          showTagsOnly
          @remove-filter="
            ({ value }) =>
              updateFiltersAndSearch({
                facet: 'tags.name',
                value,
              })
          "
        />
      </div>
      <div
        v-if="hits && hits.length"
        class="labs-experience__search-section-bottom"
      >
        <Filters
          :startCollapsed="isTablet"
          :currentContext="currentContext"
          :currentRefinedValues="currentRefinedValues"
          :filters="disjunctiveFacets"
          :features="features"
          :filterMap="filterMap"
          :filterOrders="filterOrders"
          :isFreeTrialUser="isFreeTrialUser"
          :avoidCompleting="true"
          class="labs-experience__search-section-filters"
          @update-filters="updateFiltersAndSearch"
        />
        <div class="labs-experience__search-section-items">
          <SkillDiveLabRow
            v-for="item in hits"
            :key="item.objectID"
            :content="item"
            :learningAreasAllowed="availableLearningAreas"
            @tag-selected="filterByTag"
            @content-clicked="handleContentClicked"
          />
          <div
            v-if="showBrowseMore"
            class="labs-experience__search-section-items-more"
          >
            <button class="btn btn--secondary" @click="handleBrowseMore">
              Browse more
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="!isFetching && (!hits || !hits.length)"
        class="labs-experience__search-section-no-results-wrapper"
      >
        <img
          class="labs-experience__search-section-no-results-wrapper__img"
          src="@/assets/images/status/fail-low.png"
          alt="NO RESULTS"
        />
        <h5 class="labs-experience__search-section-no-results-wrapper__error">
          Oops! We couldn't find any results matching your search.
        </h5>
        <p
          class="labs-experience__search-section-no-results-wrapper__instruction"
        >
          Please try searching for something else.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import LabsHighlightedCarrousel from '@/components/lab-experience/LabsHighlightedCarrousel.vue'
import SearchInput from '@/components/shared/SearchInput.vue'
import Filters from '@/components/search/Filters.vue'
import SkillDiveLabRow from '@/components/lab-experience/SkillDiveLabRow.vue'
import { algoliaSearchMixin, collectionCommons } from '@/helpers/mixins'
import FilterTags from '@/components/shared/FilterTags.vue'
import { getLearningAreaName } from '@/helpers'
import FeaturedCollectionsSection from '@/components/lab-experience/FeaturedCollectionsSection.vue'
import { isBackNavigation } from '@/router'
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@/helpers/localStorage'

export default {
  name: 'LabExperienceEnhanced',
  components: {
    LabsHighlightedCarrousel,
    SearchInput,
    Filters,
    SkillDiveLabRow,
    FilterTags,
    FeaturedCollectionsSection,
  },
  mixins: [algoliaSearchMixin, collectionCommons],
  data() {
    return {
      isFetching: false,
      features: [],
      currentContext: 'labs',
      allExpanded: false,
      sortingSelected: 0,
      sortingOptions: [
        { label: 'Recommended', facet: 'recommended' },
        { label: 'Name (A - Z)', facet: 'name' },
      ],
      windowWidth: window.innerWidth,
      lazyLoad: false,
      filterOrders: {
        severity: ['critical', 'high', 'medium', 'low'],
      },
    }
  },
  computed: {
    ...mapState({
      collectionsFeaturedList: state =>
        state.labExperience.collectionsFeaturedList,
      algoliaKeys: state => state.search.algoliaKeys,
      availablePasses: state => state.auth.available_passes,
      isAuthenticated: state => state.auth.isAuthenticated,
      filterMap: state => state.search.filterMap,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isBusinessUser: 'business/isBusinessUser',
      isSkillDiveB2BUser: 'auth/isSkillDiveB2BUser',
      businessUser: 'business/getBusinessUser',
      showSkillDive: 'labExperience/showSkillDive',
    }),
    hits() {
      return this.getIndexHits(this.algoliaKeys.searchIndex)
    },
    algoliaHelper() {
      return this.algoliaHelpers[this.algoliaKeys.searchIndex]
    },
    disjunctiveFacets() {
      return this.algoliaHelper?.lastResults?.disjunctiveFacets
    },
    currentRefinedValues() {
      const disjunctiveFacetsRefinements = this.algoliaHelper?.state
        .disjunctiveFacetsRefinements
      return Object.entries(disjunctiveFacetsRefinements)
    },
    currentRefinements() {
      const disjunctiveFacetsRefinements = this.algoliaHelper?.state
        .disjunctiveFacetsRefinements
      const tags = disjunctiveFacetsRefinements['tags.name'] || []
      return { tags }
    },
    filterTagsMap() {
      const disjunctiveFacetsRefinements = this.algoliaHelper?.state
        .disjunctiveFacetsRefinements
      return disjunctiveFacetsRefinements['tags.name']?.reduce(
        (acc, tag) => ({
          ...acc,
          [tag]: tag.charAt(0).toUpperCase() + tag.slice(1),
        }),
        {}
      )
    },
    isTablet() {
      return this.windowWidth <= 1024 && this.windowWidth > 768
    },
    showBrowseMore() {
      const hitsPerPage = this.algoliaHelper?.state.hitsPerPage
      const nbHits = this.algoliaHelper?.lastResults?.nbHits
      return nbHits > hitsPerPage && !this.lazyLoad
    },
    searchLabel() {
      const nbHits = this.algoliaHelper?.lastResults?.nbHits || 0
      const searchQueryLabel = this.searchQuery
        ? ` for “${this.searchQuery}”`
        : ''

      return `${nbHits} results ${searchQueryLabel}`
    },
  },
  watch: {
    hits: {
      handler(items) {
        if (items.length) {
          this.updateLabStatuses({
            ids: items.map(({ objectID }) => objectID).join(','),
          })
        }
      },
    },
  },
  async created() {
    this.cleanCurrentCollection()
    const searchIndex = this.algoliaKeys.searchIndex

    if (this.isAuthenticated) {
      this.getRunningLab()
    }

    const setup = {
      facets: ['content_type', 'is_lab_experience'],
      disjunctiveFacets: [
        'learning_areas.name',
        'vendor.name',
        'difficulty',
        'vendor.name',
        'severity',
        'tags.name',
      ],
      hitsPerPage: 12,
    }

    const options = {
      useUrl: true,
      useDebounceSearchQuery: true,
      hiddenFacets: ['content_type', 'is_lab_experience'],
    }

    const algoliaHelper = await this.initializeAlgoliaSearch(
      searchIndex,
      setup,
      options
    )

    algoliaHelper
      .addFacetRefinement('content_type', 'lab')
      .addFacetRefinement('is_lab_experience', 'true')

    if (this.availableLearningAreas?.length < 3 && !isBackNavigation()) {
      this.availableLearningAreas?.forEach(la =>
        algoliaHelper.addDisjunctiveFacetRefinement(
          'learning_areas.name',
          la.name
        )
      )
    }
    const sortOption = getLocalStorage('sortOption')

    algoliaHelper.search()
    if (isBackNavigation() && sortOption) {
      removeLocalStorage('sortOption')
      this.updateSort({ facet: sortOption })
    }
  },
  methods: {
    ...mapActions({
      getLearningPathById: 'learningPaths/getLearningPathById',
      getCourseById: 'courses/getCourseById',
      setQuery: 'search/setQuery',
      cleanCurrentCollection: 'labExperience/cleanCurrentCollection',
    }),
    ...mapActions('labs', ['getRunningLab', 'updateLabStatuses']),
    handleRowCollapsed() {
      this.allExpanded = false
    },
    handleBrowseMore() {
      const searchIndex = this.algoliaKeys.searchIndex
      this.lazyLoad = true
      this.handleAlgoliaLoadMore(searchIndex)
      this.handleAlgoliaLazyLoad(searchIndex)
    },
    filterByTag(tag) {
      this.toggleAlgoliaFilterAnd(this.algoliaKeys.searchIndex, {
        facet: 'tags.name',
        value: tag.name,
      })
    },
    updateFiltersAndSearch(filter) {
      this.toggleAlgoliaFilterAnd(this.algoliaKeys.searchIndex, filter)
    },
    updateSort(value) {
      const {
        algoliaKeys: { searchIndex, searchIndexNameAsc },
      } = this
      if (value?.facet === 'recommended') {
        this.algoliaHelper.setIndex(searchIndex)
      }
      if (value?.facet === 'name') {
        this.algoliaHelper.setIndex(searchIndexNameAsc)
      }

      this.sortingSelected = this.sortingOptions.findIndex(
        option => option.facet === value.facet
      )

      this.algoliaHelper.search()
    },

    learningAreaName(content) {
      return getLearningAreaName(content)
    },
    handleContentClicked() {
      setLocalStorage(
        'sortOption',
        this.sortingOptions[this.sortingSelected]?.facet
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-experience {
  color: var(--white);
  font-family: var(--font-medium);
  background-image: url('../../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  min-height: 100vh;
  text-align: center;

  &__loader {
    margin: auto;
    padding: 120px 20px;
    @include desktop {
      padding: 125px 100px 150px 100px;
    }
  }
  &__refinement {
    gap: 10px;
    display: flex;
    flex-direction: column;
  }
  &__search-section {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 20px;
    padding: 0 20px 120px 20px;

    max-width: 1240px;
    margin: auto;
    @include desktop {
      gap: 40px;
      padding: 0 0 150px 0;
    }

    &-results-wrapper {
      font-size: 18px;
      line-height: 24px;
      text-align: initial;
    }

    &-bottom {
      display: flex;
      flex-direction: column;
      gap: inherit;
      width: 100%;
      @include desktop {
        flex-direction: row;
      }
    }
    &-filters {
      @include desktop {
        width: 250px;
        max-width: 250px;
      }

      ::v-deep .filters {
        &__facet {
          height: 45px;
          padding: 10px;
        }

        &__title {
          display: flex;
          margin-bottom: 10px;
          @include desktop {
            display: none;
          }
        }

        &__facet-title {
          margin: auto;
          font-size: rem(16px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(24px);
        }

        &__item-count,
        &__item-title {
          font-size: rem(14px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(20px);
          text-align: left;
        }

        &__item-count {
          color: var(--mid-gray);
        }
        &__results-wrapper {
          padding: 0;
        }
      }
    }
    &-items {
      flex-grow: 2;
      display: flex;
      flex-flow: column;
      row-gap: 20px;

      &-more {
        width: 100%;
        text-align: left;
        margin-top: 30px;
      }
    }

    &-no-results-wrapper {
      display: flex;
      align-items: center;
      flex-flow: column;
      padding: 20px;
      min-height: 100vh;

      @include desktop {
        grid-row: 4;
        grid-column: 1 / 3;
      }
      &__img {
        width: 280px;
      }
      &__error {
        margin: -30px 0 0 0;
        color: var(--white);
        font-family: var(--font-bold);
        font-style: normal;
        font-weight: 700;
        font-size: rem(20px);
        line-height: rem(26px);
      }
      &__instruction {
        color: var(--light-blue-gray);
        font-family: var(--font-book);
        font-style: normal;
        font-weight: 450;
        font-size: rem(16px);
        line-height: rem(24px);
      }
    }
  }
}
</style>
